import React, { useEffect, useMemo, useRef, useState } from "react";
import { Animated, Platform, Dimensions, Pressable, ScrollView, Text, View } from "react-native";
import { useDispatch, useSelector } from "react-redux";
import MaterialIcons from "react-native-vector-icons/MaterialIcons";
import { useTheme } from "../../theme";
import { useToast } from "../../components/LhToastContainer";
import { AppDispatch, RootState } from "../../store";
import {
  setSelectedTicketId,
  setTicketDetailSource,
  saveMyTicketsListState,
  dismissUserTicketNotif,
  markUserTicketNotifRead,
} from "../../store/slices/ticketingPortalSlice";
import styles from "./MyTicketsScreenStyles";
import mobileStyles from "./MyTicketsScreen.mobile.style";
import MyTicketsHeader from "./components/MyTicketsHeader";
import MyTicketsStatusSummary from "./components/MyTicketsStatusSummary";
import MyTicketsTable from "./components/MyTicketsTable";
import MyTicketsFilterSheet, { MobileFilters } from "./components/MyTicketsFilterSheet";
import useMyTicketsListing from "./hooks/useMyTicketsListing";

type Props = {
  onNavigate: (screen: string) => void;
};

const EMPTY_FILTERS: MobileFilters = { status: "", urgency: "", category: "", assignee: "", location: "", dateRange: "" };

const MyTicketsScreen: React.FC<Props> = ({ onNavigate }) => {
  const dispatch = useDispatch<AppDispatch>();
  const { colors, resolvedTheme } = useTheme();
  const isDark = resolvedTheme === "dark";
  const toast = useToast();
  const userTicketNotifs = useSelector(
    (state: RootState) => state.ticketingPortal.userTicketNotifs
  );
  // Read the saved list state snapshot — used to extract lastOpenedTicketId for scroll restoration
  const myTicketsListState = useSelector(
    (state: RootState) => state.ticketingPortal.myTicketsListState
  );
  const {
    query,
    activeStatus,
    statusPills,
    statusCounts,
    filteredTickets,
    totalCount,
    sortColumn,
    sortDirection,
    webFilters,
    filterOptions,
    loading,
    error,
    currentPage,
    totalPages,
    setQuery,
    setActiveStatus,
    handleSort,
    setWebFilters,
    handlePageChange,
  } = useMyTicketsListing();

  const windowWidth = Dimensions.get("window").width;
  const isiPhone = Platform.OS === "ios" && windowWidth < 768;

  const [filterSheetOpen, setFilterSheetOpen] = useState(false);
  const [mobileFilters, setMobileFilters] = useState<MobileFilters>(EMPTY_FILTERS);

  /**
   * Ticket ID to scroll to after list renders (restored from Redux on Back navigation).
   * Populated once from the saved list state, then cleared to avoid re-scrolling.
   */
  const [scrollToTicketId, setScrollToTicketId] = useState<string | null>(
    () => myTicketsListState?.lastOpenedTicketId ?? null
  );

  // Once the list data has loaded after returning from Ticket Detail, allow the
  // table to perform the scroll. We keep scrollToTicketId alive until the table
  // confirms it (or the data arrives).  After one render cycle with data we clear
  // it so subsequent navigations to this screen start at the top.
  useEffect(() => {
    if (!loading && scrollToTicketId && filteredTickets.length > 0) {
      // Keep it set for one render (table handles the actual scroll), then clear.
      const timer = setTimeout(() => setScrollToTicketId(null), 800);
      return () => clearTimeout(timer);
    }
  }, [loading, filteredTickets.length, scrollToTicketId]);

  const mobileFilteredTickets = useMemo(() => {
    if (!isiPhone) return filteredTickets;
    const STATUS_KEY_MAP: Record<string, string> = {
      "New": "new", "In Progress": "progress", "Escalated to IT": "waiting-it",
      "Waiting on Vendor": "waiting-vendor", "Submitted to ADO": "submitted-ado",
      "Waiting on User": "waiting-user", "Closed": "resolved",
    };
    return filteredTickets.filter((ticket) => {
      if (mobileFilters.status) {
        const key = STATUS_KEY_MAP[mobileFilters.status];
        if (key && ticket.status !== key && ticket.status !== "waiting-tmv") return false;
      }
      if (mobileFilters.urgency &&
        ticket.urgency.toLowerCase() !== mobileFilters.urgency.toLowerCase()) return false;
      if (mobileFilters.category && ticket.category !== mobileFilters.category) return false;
      if (mobileFilters.location) {
        const locName = mobileFilters.location.split(" (SAP")[0].split(" (Market")[0].split(" (Region")[0];
        if (!ticket.location.toLowerCase().includes(locName.toLowerCase())) return false;
      }
      return true;
    });
  }, [isiPhone, filteredTickets, mobileFilters]);

  const activeFilterCount = [
    mobileFilters.status,
    mobileFilters.urgency,
    mobileFilters.category,
    mobileFilters.assignee,
    mobileFilters.location,
    mobileFilters.dateRange,
  ].filter(Boolean).length;

  const snackAnim = useRef(new Animated.Value(-80)).current;
  const [filterSnackbar, setFilterSnackbar] = useState<{ msg: string; isSuccess: boolean } | null>(null);

  const showFilterSnackbar = (msg: string, isSuccess: boolean = false) => {
    setFilterSnackbar({ msg, isSuccess });
    snackAnim.setValue(-80);
    Animated.sequence([
      Animated.timing(snackAnim, { toValue: 0, duration: 300, useNativeDriver: true }),
      Animated.delay(2500),
      Animated.timing(snackAnim, { toValue: -80, duration: 250, useNativeDriver: true }),
    ]).start(() => setFilterSnackbar(null));
  };

  /* ── Notification banner helpers ───────────────────────── */
  const unreadNotifs = userTicketNotifs.filter((n) => !n.read);

  /** Save current list state to Redux so it can be restored when the user presses Back. */
  const saveCurrentListState = (ticketId: string) => {
    dispatch(saveMyTicketsListState({
      query,
      activeStatus,
      sortColumn,
      sortDirection,
      webFilters,
      currentPage,
      lastOpenedTicketId: ticketId,
    }));
  };

  function handleNotifTicketPress(ticketId: string, notifId: string) {
    saveCurrentListState(ticketId);
    dispatch(markUserTicketNotifRead(notifId));
    dispatch(setSelectedTicketId(ticketId));
    dispatch(setTicketDetailSource("My Tickets"));
    onNavigate("Ticket Detail");
  }

  function handleDismissNotif(notifId: string) {
    dispatch(dismissUserTicketNotif(notifId));
  }

  /* ── iPhone mobile layout ────────────────────────────────── */
  if (isiPhone) {
    return (
      <View style={[mobileStyles.screenRoot, isDark && { backgroundColor: colors.background.page }]}>
        <MyTicketsHeader
          onBackToPortal={() => onNavigate("Ticketing Portal")}
          onNewTicket={() => onNavigate("Submit Ticket")}
          totalCount={mobileFilteredTickets.length}
          mobile
        />

        <ScrollView showsVerticalScrollIndicator={false}>
          <MyTicketsTable
            query={query}
            totalCount={totalCount}
            filteredTickets={mobileFilteredTickets}
            onQueryChange={setQuery}
            onRowPress={(ticketId) => {
              saveCurrentListState(ticketId);
              dispatch(setSelectedTicketId(ticketId));
              dispatch(setTicketDetailSource("My Tickets"));
              onNavigate("Ticket Detail");
            }}
            sortColumn={sortColumn}
            sortDirection={sortDirection}
            onSort={handleSort}
            webFilters={webFilters}
            onWebFilterChange={setWebFilters}
            filterOptions={filterOptions}
            mobile
            filterCount={activeFilterCount}
            onFiltersPress={() => setFilterSheetOpen(true)}
            loading={loading}
            error={error}
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={handlePageChange}
            onNewTicket={() => onNavigate("Submit Ticket")}
            onClearFilters={() => { setWebFilters({ urgency: [], status: [], category: [], subcategory: [], location: [], title: [] }); setQuery(""); }}
            scrollToTicketId={scrollToTicketId}
          />
        </ScrollView>

        <MyTicketsFilterSheet
          visible={filterSheetOpen}
          filters={mobileFilters}
          filterOptions={filterOptions}
          onApply={setMobileFilters}
          onClose={() => setFilterSheetOpen(false)}
          onSnackbar={showFilterSnackbar}
        />
        {filterSnackbar && (
          <Animated.View
            style={{
              position: "absolute",
              top: 14,
              left: 12,
              right: 12,
              zIndex: 999,
              borderRadius: 10,
              backgroundColor: filterSnackbar.isSuccess
                ? (isDark ? "#1a3d1a" : "#2d6a2d")
                : (isDark ? colors.background.elevated : "#1a2332"),
              flexDirection: "row",
              alignItems: "center",
              paddingHorizontal: 16,
              paddingVertical: 14,
              gap: 10,
              shadowColor: "#000",
              shadowOpacity: 0.2,
              shadowRadius: 8,
              shadowOffset: { width: 0, height: 4 },
              elevation: 8,
              transform: [{ translateY: snackAnim }],
            }}
          >
            <MaterialIcons name="check" size={18} color="#ffffff" />
            <Text style={{ fontSize: 15, fontWeight: "700", color: "#ffffff" }}>
              {filterSnackbar.msg}
            </Text>
          </Animated.View>
        )}
      </View>
    );
  }

  /* ── Default layout (web / iPad) ──────────────────────────── */
  return (
    <View style={[styles.screenRoot, isDark && { backgroundColor: colors.background.page }]}>
      <View style={styles.container}>
        <ScrollView style={styles.content} contentContainerStyle={styles.scrollContent}>
          <MyTicketsHeader
            onBackToPortal={() => onNavigate("Ticketing Portal")}
            onNewTicket={() => onNavigate("Submit Ticket")}
            totalCount={totalCount}
          />

          <MyTicketsStatusSummary
            activeStatus={activeStatus}
            statusPills={statusPills}
            statusCounts={statusCounts}
            onStatusChange={setActiveStatus}
          />

          <MyTicketsTable
            query={query}
            totalCount={totalCount}
            filteredTickets={filteredTickets}
            onQueryChange={setQuery}
            onRowPress={(ticketId) => {
              saveCurrentListState(ticketId);
              dispatch(setSelectedTicketId(ticketId));
              dispatch(setTicketDetailSource("My Tickets"));
              onNavigate("Ticket Detail");
            }}
            sortColumn={sortColumn}
            sortDirection={sortDirection}
            onSort={handleSort}
            webFilters={webFilters}
            onWebFilterChange={setWebFilters}
            filterOptions={filterOptions}
            loading={loading}
            error={error}
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={handlePageChange}
            onNewTicket={() => onNavigate("Submit Ticket")}
            onClearFilters={() => { setWebFilters({ urgency: [], status: [], category: [], subcategory: [], location: [], title: [] }); setQuery(""); }}
            scrollToTicketId={scrollToTicketId}
          />
        </ScrollView>
      </View>
    </View>
  );
};

import { StyleSheet } from "react-native";

/** Styles for waiting-user notification banners */
const ntfStyles = StyleSheet.create({
  bannerWeb: {
    flexDirection: "row",
    alignItems: "flex-start",
    gap: 10,
    backgroundColor: "#FFF5E6",
    borderWidth: 1,
    borderColor: "#F2B870",
    borderRadius: 8,
    paddingHorizontal: 16,
    paddingVertical: 12,
    marginBottom: 10,
  },
  bannerMobile: {
    flexDirection: "row",
    alignItems: "flex-start",
    gap: 10,
    backgroundColor: "#FFF5E6",
    borderWidth: 1,
    borderColor: "#F2B870",
    borderRadius: 8,
    paddingHorizontal: 14,
    paddingVertical: 10,
    marginHorizontal: 12,
    marginBottom: 8,
    marginTop: 4,
  },
  bannerBody: {
    flex: 1,
  },
  bannerMessage: {
    fontSize: 13,
    color: "#5C3100",
    fontWeight: "500",
    lineHeight: 18,
  },
  bannerLink: {
    fontSize: 13,
    color: "#034895",
    fontWeight: "700",
    textDecorationLine: "underline",
    ...Platform.select({ web: { cursor: "pointer" } as any }),
  },
  bannerChannels: {
    marginTop: 3,
    fontSize: 11,
    color: "#A85100",
    fontWeight: "500",
  },
  bannerClose: {
    padding: 2,
    ...Platform.select({ web: { cursor: "pointer" } as any }),
  },
  /** Override background/border for merge notifications (purple tint instead of amber) */
  bannerMerge: {
    backgroundColor: "#F0EFFD",
    borderColor: "#B3B0E8",
  },
});

export default MyTicketsScreen;
